import {
  Avatar,
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Form,
  Input,
  Modal,
  Popover,
  Select,
  Skeleton,
  Tag,
  Tooltip,
  Alert,
} from 'antd';
import clsx from 'clsx';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import type GroupedFileAccess from 'interfaces/GroupedFileAccess';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { useTranslation } from 'react-i18next';
import { UserRoleEnum } from '../../../enums/UserRoleEnum';
import { UnsavedChangesScopeEnum } from '../../../enums/UnsavedChangesScopeEnum';
import useFileShareForm from '../../../hooks/useFileShareForm';
import { useRequestLeave } from '../../../hooks/useRequestLeave';
import { useSnapshotDirtyState } from '../../../hooks/useSnapshotDirtyState';
import { createGuardedModalCancelHandler } from '../../../utils/openGuardedFormModal';
import useFormatTimestamp from '../../../hooks/useFormatTimestamp';
import type File from '../../../interfaces/File';
import type FileAccess from '../../../interfaces/FileAccess';
import type { FileAccessNotificationSetting } from '../../../interfaces/FileAccessNotificationSetting';
import type Group from '../../../interfaces/Group';
import type User from '../../../interfaces/User';
import { getAvatarColor } from '../../../utils/avatarColors';
import {
  computeCanElevateToEditor,
  getEditorOptionDisabledReason,
  isDirectShareEditorOptionDisabled,
  isDirectShareExpirationPickerEnabled,
} from '../../../utils/fileShareEditability';
import {
  dateFromTicks,
  isTicksBeforeNow,
  localDateFromUtcTicks,
  msToTicks,
  ticksToMs,
} from '../../../utils/utils';
import KeyValuePopoverGrid from '../../Common/KeyValuePopoverGrid';
import ListyMetaRow from '../../Common/ListyMetaRow';
import ListyWithEmpty from '../../Common/ListyWithEmpty';
import TruncatedTooltip from '../../Common/TruncatedTooltip';
import UserMultiSelect from '../../Common/UserMultiSelect';
import { Icon } from '../../Core/Icon';
dayjs.extend(utc);

interface Props {
  selectedFiles: File[];
  onSubmit: (
    newUsers: User[],
    newGroups: Group[],
    fileAccessesToRemove: FileAccess[],
    fileAccessesToUpdate: FileAccess[],
    fileAccessesRevokeDenied: FileAccess[],
    sendMessage: boolean,
    message: string,
    readOnly: boolean,
    expiry: number,
    closeModalAfterSubmit: boolean,
    notificationSettings: FileAccessNotificationSetting | null,
    recipientNotificationOverrides: Record<string, FileAccessNotificationSetting | null>,
    currentAccessesForPayload: FileAccess[],
  ) => void;
  onCancel: () => void;
}

export const FileShareForm = ({ selectedFiles, onSubmit, onCancel }: Props) => {
  const { t } = useTranslation();
  const requestLeave = useRequestLeave();
  const formatTimestamp = useFormatTimestamp();
  const allSelectedFilesWritable =
    selectedFiles.length > 0 && selectedFiles.every((file) => !file.isReadOnly);
  const {
    form,
    isLimitationsDatePickerOpen,
    limitationsDatePickerValue,
    isFileAccessDatePickerOpen,
    fileAccessDatePickerValue,
    selectedFileAccessesForDatePicker,
    isLoading,
    isEditAllowed,
    performedByUser,
    openPerformedByUserPopoverUuid,
    isLoadingPerformedByUser,
    scrollableDivRef,
    handleScroll,
    handleSubmit,
    handleFormFieldChange,
    handleFileLimitationChange,
    isFileAccessMarkedToRemove,
    isFileAccessMarkedToRevokeDeny,
    isFileAccessUserMe,
    areNewUsersOrGroupsSelected,
    isSubmitButtonDisabled,
    submitDisabledReason,
    existingAccessesRequireExpirationError,
    canApplyMaxExpirationToMissingAccesses,
    handleApplyMaxExpirationToAllMissing,
    perAccessExpirationPickersAllowBulkMaxExpiration,
    fileOwners,
    fileOwnerIds,
    fileOwnersEmails,
    handleOpenPerformedByUserDetails,
    groupedFileAccesses,
    originalGroupedFileAccesses,
    newSharingLimits,
    newSharingDatePickerMaxDate,
    setIsLimitationsDatePickerOpen,
    contextHolder,
    setLimitationsDatePickerValue,
    setIsFileAccessDatePickerOpen,
    setFileAccessDatePickerValue,
    setSelectedFileAccessesForDatePicker,
    areAllSelectedFilesMine,
    setOpenPerformedByUserPopoverUuid,
    copyFileLinkToClipboard,
    getMaxDateForFileAccessPicker,
    getMaxDateForExistingFileAccess,
    hasMandatoryExpirationRestriction,
    isSubjectBoundByMandatoryExpiration,
    handleShareAgain,
    handleCancelShareAgain,
    filesMaxExpirationDays,
    messageApi,
    isModalLoading,
    authenticatedUser,
    haveExistingAccessesChanged,
    handleResetForm,
    setFilters,
    openFileAccessNotificationModal,
    haveNotificationSettingsChanged,
  } = useFileShareForm({ selectedFiles, onSubmit });

  const isShareFormDirty =
    haveExistingAccessesChanged || areNewUsersOrGroupsSelected || haveNotificationSettingsChanged;

  useSnapshotDirtyState({
    isDirty: isShareFormDirty,
    onDiscard: () => handleResetForm(),
    scope: UnsavedChangesScopeEnum.Modal,
  });

  const handleCancel = createGuardedModalCancelHandler({
    onConfirmed: onCancel,
    requestLeave,
    resetForm: () => handleResetForm(),
  });

  return (
    <Form
      layout="vertical"
      size="large"
      className="pt-6 [&_.ant-form-item]:mb-2 [&_.ant-form-item-explain-error]:hidden [&_.ant-form-item-label]:hidden"
      aria-label={t('modal.fileShareForm.ariaLabel')}
      role="form"
    >
      {contextHolder}
      <div className="flex flex-col gap-2">
        <div
          className={clsx(
            'divide-gray-medium flex w-full flex-row items-center divide-x rounded-md',
            areNewUsersOrGroupsSelected ? 'border-gray-medium border' : 'border-transparent',
          )}
          role="group"
          aria-label={t('modal.fileShareForm.selectUsersOrGroups')}
        >
          <UserMultiSelect
            className="w-full p-0"
            variant={areNewUsersOrGroupsSelected ? 'borderless' : 'outlined'}
            ignoredUserIds={[...fileOwnerIds, authenticatedUser?.id || 0]}
            forbiddenTagNames={[...fileOwnersEmails, authenticatedUser?.email].filter(
              (v): v is string => !!v,
            )}
            closeOnSelect
            size="large"
            maxTagCount={10}
            allowClear={false}
            disabled={isLoading}
            selectedUserIds={form.users.map((user) => user?.id)}
            selectedGroupIds={form.groups.map((group) => group?.id)}
            selectedUsers={form.users}
            selectedGroups={form.groups}
            onUserChange={(_, values) => {
              const alreadySelectedUsersOrGroups = form.users.length > 0 || form.groups.length > 0;
              const noMoreUsersOrGroupsSelected = form.groups.length === 0 && values.length === 0;

              if (noMoreUsersOrGroupsSelected) {
                handleResetForm(true);
                setFilters({
                  offset: 0,
                  count: 100,
                  search: '',
                  files: selectedFiles.map((file) => file.id.toString()),
                });
              }

              // Check if there are any changes to already present accesses
              if (haveExistingAccessesChanged && !alreadySelectedUsersOrGroups) {
                Modal.confirm({
                  title: t('modal.fileShareForm.existingAccessesChanged'),
                  content: t('modal.fileShareForm.existingAccessesChangedDescription'),
                  centered: true,
                  onOk: () => {
                    handleSubmit(false);
                    handleResetForm();
                    handleFormFieldChange('users', values);
                  },
                  // Abort add: keep existing access edits; controlled picker snaps back to form.users
                  onCancel: () => {},
                  okText: t('common.saveAndContinue'),
                  cancelText: t('common.cancel'),
                });
              } else {
                handleFormFieldChange('users', values);
              }
            }}
            onGroupChange={(_, values) => {
              const alreadySelectedUsersOrGroups = form.users.length > 0 || form.groups.length > 0;
              const noMoreUsersOrGroupsSelected = form.users.length === 0 && values.length === 0;

              if (noMoreUsersOrGroupsSelected) {
                handleResetForm(true);
                setFilters({
                  offset: 0,
                  count: 100,
                  search: '',
                  files: selectedFiles.map((file) => file.id.toString()),
                });
              }

              if (haveExistingAccessesChanged && !alreadySelectedUsersOrGroups) {
                Modal.confirm({
                  title: t('modal.fileShareForm.existingAccessesChanged'),
                  content: t('modal.fileShareForm.existingAccessesChangedDescription'),
                  centered: true,
                  okText: t('common.saveAndContinue'),
                  cancelText: t('common.cancel'),
                  onOk: () => {
                    handleSubmit(false);
                    handleResetForm();
                    handleFormFieldChange('groups', values);
                  },
                  // Abort add: keep existing access edits; controlled picker snaps back to form.groups
                  onCancel: () => {},
                });
              } else {
                handleFormFieldChange('groups', values);
              }
            }}
            additionalUserFilters={{
              roleFilter: [
                UserRoleEnum.SuperAdmin,
                UserRoleEnum.Admin,
                UserRoleEnum.Contributor,
                UserRoleEnum.Reader,
                UserRoleEnum.External,
              ],
              excludeFileIds: selectedFiles.map((file) => file.id),
            }}
            additionalGroupFilters={{
              excludeFileIds: selectedFiles.map((file) => file.id),
            }}
            mode="tags"
            aria-label={t('modal.fileShareForm.userMultiSelectAria')}
          />
          {areNewUsersOrGroupsSelected && (
            <Select
              className="p-0"
              variant="borderless"
              placement="bottomRight"
              value={form.readOnly}
              onChange={(value) => handleFormFieldChange('readOnly', value)}
              optionRender={(option) => {
                return (
                  <div className="flex w-full min-w-32 flex-row items-center gap-2">
                    {option.data.icon}
                    <span>{option.data.label}</span>
                  </div>
                );
              }}
              popupMatchSelectWidth={false}
              labelRender={() => {
                return (
                  <Tooltip title={form.readOnly ? t('common.viewer') : t('common.editor')}>
                    <div>
                      <Icon
                        name={form.readOnly ? 'ViewIcon' : 'SquarePenIcon'}
                        className={clsx(
                          'h-6 w-6',
                          form.readOnly ? 'text-yellow-dark' : 'text-green-dark',
                        )}
                        aria-hidden="true"
                      />
                    </div>
                  </Tooltip>
                );
              }}
              popupRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />

                  <Button
                    type="text"
                    size="small"
                    onClick={() => {
                      setIsLimitationsDatePickerOpen(true);
                      if (form.expiry) {
                        setLimitationsDatePickerValue(localDateFromUtcTicks(form.expiry));
                      } else {
                        setLimitationsDatePickerValue(null);
                      }
                    }}
                    aria-label={
                      form.expiry ? t('common.editExpirationDate') : t('common.addExpirationDate')
                    }
                  >
                    <div className="flex flex-row items-center gap-2">
                      <Icon name="CalendarFoldIcon" className="h-5 w-5" aria-hidden="true" />
                      <span>
                        {form.expiry
                          ? t('common.editExpirationDate')
                          : t('common.addExpirationDate')}
                      </span>
                    </div>
                  </Button>
                </>
              )}
              options={[
                {
                  value: true,
                  icon: (
                    <Icon name="ViewIcon" className="text-yellow-dark h-5 w-5" aria-hidden="true" />
                  ),
                  label: t('common.viewer'),
                  'aria-label': t('common.viewer'),
                },
                {
                  value: false,
                  icon: (
                    <Icon
                      name="SquarePenIcon"
                      className="text-green-dark h-5 w-5"
                      aria-hidden="true"
                    />
                  ),
                  label: t('common.editor'),
                  disabled: !newSharingLimits.canEditAllowed,
                  title: !newSharingLimits.canEditAllowed
                    ? t(
                        getEditorOptionDisabledReason({
                          newShareCanEditAllowed: newSharingLimits.canEditAllowed,
                        }) ?? 'common.editorDisabledByPolicy',
                      )
                    : undefined,
                  'aria-label': t('common.editor'),
                },
              ]}
              aria-label={form.readOnly ? t('common.viewer') : t('common.editor')}
              aria-expanded="true"
              aria-haspopup="listbox"
            />
          )}
        </div>

        {areNewUsersOrGroupsSelected && form.expiry !== 0 && (
          <div
            className="flex flex-row items-center justify-start gap-2"
            role="group"
            aria-label={t('common.expirationDate')}
          >
            <span className="text-gray-dark text-sm">{t('common.expirationDate')}:</span>
            <Tooltip title={t('common.removeExpirationDate')}>
              <Button
                className="py-0! text-xs!"
                onClick={() => {
                  // Clear only when policy does not force a max (all recipients exempt / no DenyFileUnlimitedExpiration).
                  // When newSharingDatePickerMaxDate is set, button stays disabled — see disabled prop.
                  if (!newSharingDatePickerMaxDate) {
                    handleFormFieldChange('expiry', 0);
                  }
                }}
                size="small"
                disabled={!form.expiry || !!newSharingDatePickerMaxDate}
                aria-label={t('common.removeExpirationDate')}
              >
                <div className="flex flex-row items-center gap-2">
                  <span>{dayjs.utc(ticksToMs(form.expiry)).format('YYYY-MM-DD HH:mm [UTC]')}</span>
                  <Icon name="XIcon" className="h-5 w-5" aria-hidden="true" />
                </div>
              </Button>
            </Tooltip>
          </div>
        )}
      </div>
      {areNewUsersOrGroupsSelected ? (
        <div
          className="mt-4 flex flex-col gap-2"
          role="region"
          aria-label={t('modal.fileShareForm.sendMessage')}
        >
          <Checkbox
            checked={form.sendMessage}
            onChange={(e) => handleFormFieldChange('sendMessage', e.target.checked)}
            aria-checked={form.sendMessage}
            aria-label={t('modal.fileShareForm.sendMessage')}
          >
            {t('modal.fileShareForm.sendMessage')}
          </Checkbox>
          {form.sendMessage && (
            <Input.TextArea
              value={form.message}
              onChange={(e) => handleFormFieldChange('message', e.target.value)}
              rows={4}
              placeholder={t('modal.fileShareForm.sendMessagePlaceholder')}
              aria-label={t('modal.fileShareForm.sendMessagePlaceholder')}
            />
          )}
        </div>
      ) : (
        <div
          className="flex flex-col gap-2"
          role="region"
          aria-label={t('modal.fileShareForm.peopleWithAccess')}
        >
          <Divider aria-hidden="true" />
          <span className="text-lg font-medium" id="people-with-access-label">
            {t('modal.fileShareForm.peopleWithAccess')}
          </span>
          <OverlayScrollbarsComponent
            options={{
              scrollbars: {
                autoHide: 'leave',
              },
            }}
            defer
            style={{ height: '100%', overflow: 'auto' }}
            aria-labelledby="people-with-access-label"
            role="region"
          >
            <div
              ref={scrollableDivRef}
              onScroll={handleScroll}
              className="mb-4 flex max-h-80 flex-col overflow-y-auto pr-2"
              role="group"
              aria-label={t('modal.fileShareForm.peopleWithAccess')}
            >
              {fileOwners.length > 0 && (
                <div
                  className="border-gray-medium/30 flex flex-row items-center justify-between border-b py-2.5"
                  aria-label={t('common.owner')}
                >
                  <div className="flex flex-row items-center gap-3">
                    <Avatar
                      size={42}
                      className={clsx('bg-azure-hover!')}
                      aria-label={t('common.owner')}
                    >
                      <Icon name="UserStarIcon" className="h-5 w-5" aria-hidden="true" />
                    </Avatar>
                    <div className="flex flex-row items-center gap-2">
                      <div className="flex flex-col gap-0.5">
                        <span className="max-w-80 truncate">
                          <TruncatedTooltip>
                            {fileOwners.length > 1 ? t('common.multipleOwners') : t('common.owner')}
                          </TruncatedTooltip>
                        </span>
                        {fileOwners.length === 1 && (
                          <span className="text-sm text-black/50">{fileOwners[0]}</span>
                        )}
                      </div>
                      {fileOwners.length > 1 ? (
                        <Tooltip title={fileOwners.join(', ')}>
                          <div
                            className="bg-azure-hover flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white"
                            aria-label={t('common.moreOwners')}
                          >
                            <span className="text-sm">+{fileOwners.length}</span>
                          </div>
                        </Tooltip>
                      ) : null}
                    </div>
                  </div>
                </div>
              )}
              <div aria-label={t('modal.fileShareForm.peopleWithAccess')}>
                <ListyWithEmpty<GroupedFileAccess>
                  items={groupedFileAccesses}
                  loading={isLoading}
                  emptyText={t('common.noUsers')}
                  rowKey="uuid"
                  itemRender={(grouped) => {
                    let subject: User | Group;
                    let isUser: boolean = false;

                    if (grouped.subject.type === 'user') {
                      subject = grouped.subject.user;
                      isUser = true;
                    } else {
                      subject = grouped.subject.group;
                      isUser = false;
                    }

                    const user = grouped.subject.type === 'user' ? grouped.subject.user : null;
                    const group = grouped.subject.type === 'group' ? grouped.subject.group : null;

                    let name = isUser
                      ? user?.firstName
                        ? `${user.firstName} ${user.lastName}`
                        : user?.email
                      : group?.name;

                    if (isUser && user?.id === authenticatedUser?.id) {
                      name = `${user?.firstName} ${user?.lastName} (${t('common.me')})`;
                    }

                    const avatarLabel = isUser ? (
                      user?.firstName || user?.lastName ? (
                        <>
                          {user?.firstName?.charAt(0)?.toUpperCase()}
                          {user?.lastName?.charAt(0)?.toUpperCase()}
                        </>
                      ) : (
                        <Icon name="AtSignIcon" className="h-5 w-5" aria-hidden="true" />
                      )
                    ) : (
                      <Icon name="UsersIcon" className="h-5 w-5" aria-hidden="true" />
                    );

                    const description = isUser ? user?.email : group?.name;

                    const mainFileAccess = grouped.originalFileAccesses[0];

                    // Determine approval status based on the highest value among approvalRequest, approved, and notApproved
                    const getFileShareRequestApprovalStatus = (
                      fileAccess: typeof mainFileAccess,
                    ) => {
                      if (!fileAccess) return null;

                      const approvalRequest = fileAccess.approvalRequest || 0;
                      const approved = fileAccess.approved || 0;
                      const notApproved = fileAccess.notApproved || 0;

                      // If all are 0, no approval request exists
                      if (approvalRequest === 0 && approved === 0 && notApproved === 0) {
                        return null;
                      }

                      // Find the maximum value
                      const maxValue = Math.max(approvalRequest, approved, notApproved);

                      // Determine status based on which value is the maximum
                      if (maxValue === approved && approved > 0) {
                        return { status: 'approved', color: 'blue', dateTicks: approved };
                      } else if (maxValue === notApproved && notApproved > 0) {
                        return { status: 'rejected', color: 'red', dateTicks: notApproved };
                      } else if (maxValue === approvalRequest && approvalRequest > 0) {
                        return { status: 'pending', color: 'green', dateTicks: approvalRequest };
                      }

                      return null;
                    };

                    const fileShareRequestApprovalStatus =
                      getFileShareRequestApprovalStatus(mainFileAccess);

                    const allPerformedBy = grouped.originalFileAccesses.map(
                      (fa) => fa.performedByUserId,
                    );

                    const firstPerformedBy = allPerformedBy[0];
                    const performedByUserId = allPerformedBy.every((id) => id === firstPerformedBy)
                      ? firstPerformedBy
                      : 'mixed';

                    const isAnyMarkedToRemove = grouped.originalFileAccesses.some(
                      isFileAccessMarkedToRemove,
                    );

                    const isAnyMarkedToRevokeDeny = grouped.originalFileAccesses.some(
                      isFileAccessMarkedToRevokeDeny,
                    );

                    const originalGroup = originalGroupedFileAccesses.find(
                      (g) =>
                        g.subject.type === grouped.subject.type &&
                        ((g.subject.type === 'user' &&
                          grouped.subject.type === 'user' &&
                          g.subject.user.id === grouped.subject.user?.id) ||
                          (g.subject.type === 'group' &&
                            grouped.subject.type === 'group' &&
                            g.subject.group.id === grouped.subject.group?.id)),
                    );

                    const allOriginalAreEditor =
                      originalGroup?.originalFileAccesses.every(
                        (fa) => fa.fileLimitation.canEdit === true,
                      ) ?? false;

                    const allPerformedByMe = grouped.originalFileAccesses.every(
                      (fa) => fa.performedByUserId === authenticatedUser?.id,
                    );

                    const canElevateToEditor = computeCanElevateToEditor(
                      areAllSelectedFilesMine,
                      allOriginalAreEditor,
                      allPerformedByMe,
                    );

                    const disableEditorOption = isDirectShareEditorOptionDisabled(
                      isEditAllowed,
                      allSelectedFilesWritable,
                      canElevateToEditor,
                    );
                    const editorDisabledReasonKey = getEditorOptionDisabledReason({
                      cannotElevateReceivedShare: disableEditorOption,
                    });
                    const editorDisabledTitle = editorDisabledReasonKey
                      ? t(editorDisabledReasonKey)
                      : undefined;

                    // Mixed may return (per-node override probe): keep disabled Mixed option commented for restore.
                    // const canEditOptions =
                    //   grouped.canEdit === 'mixed'
                    //     ? [
                    //         {
                    //           value: 'mixed',
                    //           label: (
                    //             <div className="flex flex-row items-center">
                    //               <Icon
                    //                 name="BlendIcon"
                    //                 className="mt-0.5 h-5 shrink-0"
                    //                 aria-hidden="true"
                    //               />
                    //               <span className="truncate">{t('common.mixed')}</span>
                    //             </div>
                    //           ),
                    //           disabled: true,
                    //           'aria-label': t('common.mixed'),
                    //         },
                    //         {
                    //           value: false,
                    //           label: (
                    //             <div className="flex flex-row items-center">
                    //               <Icon
                    //                 name="ViewIcon"
                    //                 className="text-yellow-dark mt-0.5 h-5 shrink-0"
                    //                 aria-hidden="true"
                    //               />
                    //               <span className="truncate">{t('common.viewer')}</span>
                    //             </div>
                    //           ),
                    //           'aria-label': t('common.viewer'),
                    //         },
                    //         {
                    //           value: true,
                    //           label: (
                    //             <div className="flex flex-row items-center">
                    //               <Icon
                    //                 name="SquarePenIcon"
                    //                 className="text-green-dark mt-0.5 h-5 shrink-0"
                    //                 aria-hidden="true"
                    //               />
                    //               <span className="truncate">{t('common.editor')}</span>
                    //             </div>
                    //           ),
                    //           disabled: disableEditorOption,
                    //           title: editorDisabledTitle,
                    //           'aria-label': t('common.editor'),
                    //         },
                    //       ]
                    //     : [ /* Viewer + Editor only */ ];
                    const canEditOptions = [
                      {
                        value: false,
                        label: (
                          <div className="flex flex-row items-center">
                            <Icon
                              name="ViewIcon"
                              className="text-yellow-dark mt-0.5 h-5"
                              aria-hidden="true"
                            />
                            <span>{t('common.viewer')}</span>
                          </div>
                        ),
                        'aria-label': t('common.viewer'),
                      },
                      {
                        value: true,
                        label: (
                          <div className="flex flex-row items-center">
                            <Icon
                              name="SquarePenIcon"
                              className="text-green-dark mt-0.5 h-5"
                              aria-hidden="true"
                            />
                            <span>{t('common.editor')}</span>
                          </div>
                        ),
                        disabled: disableEditorOption,
                        title: editorDisabledTitle,
                        'aria-label': t('common.editor'),
                      },
                    ];
                    return (
                      <ListyMetaRow
                        avatar={
                          <Avatar
                            size={42}
                            className={clsx(isAnyMarkedToRemove && 'bg-red-dark!')}
                            style={
                              isAnyMarkedToRemove
                                ? undefined
                                : { backgroundColor: getAvatarColor(subject.id) }
                            }
                            aria-label={name}
                          >
                            <span className="text-base">{avatarLabel}</span>
                          </Avatar>
                        }
                        title={
                          <div className="flex flex-row items-center gap-2">
                            <span
                              className={clsx(
                                isAnyMarkedToRemove && 'line-through',
                                isEditAllowed &&
                                  grouped.originalFileAccesses.some(
                                    (fa) => fa.isInherited && fa.fileLimitation.accessDenied,
                                  ) &&
                                  !isAnyMarkedToRevokeDeny &&
                                  'line-through',
                              )}
                            >
                              {name}
                            </span>
                            <Tooltip title={t('fileAccessNotification.configureNotifications')}>
                              <Button
                                type="text"
                                size="small"
                                icon={<Icon name="BellIcon" className="h-4 w-4" />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Use id for existing accesses (matches API-loaded overrides), else email/name for new
                                  const recipientKey = isUser
                                    ? `user-${user?.id ?? user?.email ?? ''}`
                                    : `group-${group?.id ?? group?.name ?? ''}`;
                                  const recipientLabel = isUser
                                    ? (user?.email ?? '')
                                    : (group?.name ?? '');
                                  const fileAccessNotificationOverrideTarget = {
                                    recipientKey,
                                    recipientLabel,
                                  };
                                  openFileAccessNotificationModal(
                                    fileAccessNotificationOverrideTarget,
                                  );
                                }}
                                disabled={isLoading || isModalLoading}
                                aria-label={t('fileAccessNotification.configureNotifications')}
                                className="shrink-0"
                              />
                            </Tooltip>
                            {fileShareRequestApprovalStatus && (
                              <Tooltip
                                title={
                                  fileShareRequestApprovalStatus.dateTicks
                                    ? (() => {
                                        const formattedDate = formatTimestamp(
                                          dateFromTicks(fileShareRequestApprovalStatus.dateTicks),
                                        );
                                        if (fileShareRequestApprovalStatus.status === 'pending') {
                                          return t('common.pendingApprovalTooltip', {
                                            date: formattedDate,
                                          });
                                        } else if (
                                          fileShareRequestApprovalStatus.status === 'approved'
                                        ) {
                                          return t('common.approvedByTeamTooltip', {
                                            date: formattedDate,
                                          });
                                        } else if (
                                          fileShareRequestApprovalStatus.status === 'rejected'
                                        ) {
                                          return t('common.rejectedByTeamTooltip', {
                                            date: formattedDate,
                                          });
                                        }
                                        return formattedDate;
                                      })()
                                    : ''
                                }
                              >
                                <Tag
                                  variant="outlined"
                                  color={fileShareRequestApprovalStatus.color}
                                >
                                  {fileShareRequestApprovalStatus.status === 'approved'
                                    ? t('common.approved')
                                    : fileShareRequestApprovalStatus.status === 'rejected'
                                      ? t('common.rejected')
                                      : t('common.pending')}
                                </Tag>
                              </Tooltip>
                            )}
                          </div>
                        }
                        description={
                          <div className="flex h-6 flex-row items-center justify-between gap-2">
                            <Tooltip title={description}>
                              <span className="truncate text-sm">{description}</span>
                            </Tooltip>
                            {(() => {
                              if (
                                grouped.accessExpiration !== 'mixed' &&
                                grouped.accessExpiration > 0
                              ) {
                                return (
                                  <div className="flex flex-row items-center gap-0.5">
                                    <span
                                      className={clsx(
                                        'text-xs',
                                        isTicksBeforeNow(grouped.accessExpiration) &&
                                          'text-red-dark font-semibold',
                                      )}
                                    >
                                      {isTicksBeforeNow(grouped.accessExpiration)
                                        ? t('common.expired')
                                        : t('common.expiry')}
                                      :
                                    </span>
                                    <Tooltip title={t('common.removeExpirationDate')}>
                                      <Button
                                        size="small"
                                        type="text"
                                        className={clsx(
                                          'py-0! text-xs!',
                                          isTicksBeforeNow(grouped.accessExpiration) &&
                                            'text-red-dark font-semibold',
                                        )}
                                        disabled={
                                          areAllSelectedFilesMine
                                            ? isSubjectBoundByMandatoryExpiration(grouped.subject)
                                            : isSubjectBoundByMandatoryExpiration(
                                                grouped.subject,
                                              ) ||
                                              (hasMandatoryExpirationRestriction() &&
                                                typeof filesMaxExpirationDays !== 'number') ||
                                              grouped.originalFileAccesses.some(
                                                (fa) => fa.fileLimitation.canEdit === false,
                                              ) ||
                                              grouped.originalFileAccesses.some(
                                                (fa) =>
                                                  fa.performedByUserId !== authenticatedUser?.id,
                                              )
                                        }
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          const boundByFilePolicy =
                                            isSubjectBoundByMandatoryExpiration(grouped.subject);
                                          const boundByReshareExpiry =
                                            !areAllSelectedFilesMine &&
                                            hasMandatoryExpirationRestriction() &&
                                            typeof filesMaxExpirationDays !== 'number';
                                          if (!boundByFilePolicy && !boundByReshareExpiry) {
                                            grouped.originalFileAccesses.forEach((fa) =>
                                              handleFileLimitationChange(fa, 'accessExpiration', 0),
                                            );
                                          }
                                        }}
                                        aria-label={t('common.removeExpirationDate')}
                                      >
                                        {dayjs
                                          .utc(ticksToMs(grouped.accessExpiration))
                                          .format('YYYY-MM-DD HH:mm [UTC]')}
                                      </Button>
                                    </Tooltip>
                                  </div>
                                );
                              }
                              if (grouped.accessExpiration === 'mixed') {
                                if (allPerformedByMe) {
                                  return (
                                    <>
                                      <Tooltip title={t('common.removeExpirationDate')}>
                                        <Button
                                          type="text"
                                          size="small"
                                          className="py-0! text-xs!"
                                          disabled={
                                            isSubjectBoundByMandatoryExpiration(grouped.subject) ||
                                            (hasMandatoryExpirationRestriction() &&
                                              typeof filesMaxExpirationDays !== 'number')
                                          }
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            const boundByFilePolicy =
                                              isSubjectBoundByMandatoryExpiration(grouped.subject);
                                            const boundByReshareExpiry =
                                              hasMandatoryExpirationRestriction() &&
                                              typeof filesMaxExpirationDays !== 'number';
                                            if (!boundByFilePolicy && !boundByReshareExpiry) {
                                              grouped.originalFileAccesses.forEach((fa) =>
                                                handleFileLimitationChange(
                                                  fa,
                                                  'accessExpiration',
                                                  0,
                                                ),
                                              );
                                            }
                                          }}
                                          aria-label={t('common.removeExpirationDate')}
                                        >
                                          {t('common.multipleExpiryDates')}
                                          <Icon
                                            name="XIcon"
                                            className="h-5 w-5"
                                            aria-hidden="true"
                                          />
                                        </Button>
                                      </Tooltip>
                                    </>
                                  );
                                } else {
                                  return (
                                    <Tooltip title={t('common.multipleExpiryDates')}>
                                      <Button
                                        type="text"
                                        size="small"
                                        disabled
                                        className="py-0! text-xs!"
                                        aria-label={t('common.multipleExpiryDates')}
                                      >
                                        {t('common.multipleExpiryDates')}
                                      </Button>
                                    </Tooltip>
                                  );
                                }
                              }
                              return null;
                            })()}
                          </div>
                        }
                        actions={
                          isAnyMarkedToRemove ? (
                            <Button
                              variant="text"
                              color="primary"
                              size="small"
                              onClick={() => {
                                handleFormFieldChange(
                                  'fileAccessesToRemove',
                                  form.fileAccessesToRemove.filter(
                                    (f) => !grouped.fileAccessIds.includes(f.id),
                                  ),
                                );
                              }}
                              aria-label={t('common.cancel')}
                            >
                              {t('common.cancel')}
                            </Button>
                          ) : (
                            <div className="flex flex-row items-center gap-0.5">
                              {performedByUserId !== 0 &&
                                performedByUserId !== authenticatedUser?.id &&
                                performedByUserId !== undefined && (
                                  <div className="border-gray-medium mr-2 border-r pr-2 pl-1">
                                    {performedByUserId === 'mixed' ? (
                                      <Tooltip title={t('common.mixed')}>
                                        <div>
                                          <Icon
                                            name="WaypointsIcon"
                                            className="mt-0.5 h-5"
                                            aria-hidden="true"
                                          />
                                        </div>
                                      </Tooltip>
                                    ) : (
                                      <Tooltip title={t('common.sharedBy')}>
                                        <Popover
                                          content={
                                            <KeyValuePopoverGrid
                                              rows={[
                                                {
                                                  label: t('common.firstName'),
                                                  value: isLoadingPerformedByUser
                                                    ? t('common.loading')
                                                    : performedByUser?.firstName,
                                                  valueClassName: clsx(
                                                    'font-medium transition-all duration-300',
                                                    isLoadingPerformedByUser && 'blur-sm',
                                                  ),
                                                },
                                                {
                                                  label: t('common.lastName'),
                                                  value: isLoadingPerformedByUser
                                                    ? t('common.loading')
                                                    : performedByUser?.lastName,
                                                  valueClassName: clsx(
                                                    'font-medium transition-all duration-200',
                                                    isLoadingPerformedByUser && 'blur-sm',
                                                  ),
                                                },
                                                {
                                                  label: t('common.email'),
                                                  value: isLoadingPerformedByUser
                                                    ? t('common.loading')
                                                    : performedByUser?.email,
                                                  valueClassName: clsx(
                                                    'font-medium transition-all duration-200',
                                                    isLoadingPerformedByUser && 'blur-sm',
                                                  ),
                                                },
                                              ]}
                                            />
                                          }
                                          title={t('common.sharedBy')}
                                          trigger="click"
                                          open={openPerformedByUserPopoverUuid === grouped.uuid}
                                          onOpenChange={() => {
                                            setOpenPerformedByUserPopoverUuid(null);
                                          }}
                                          aria-label={t('common.sharedBy')}
                                        >
                                          <Button
                                            type="text"
                                            size="small"
                                            onClick={() =>
                                              handleOpenPerformedByUserDetails(
                                                performedByUserId,
                                                grouped.uuid,
                                              )
                                            }
                                            aria-label={t('common.sharedBy')}
                                          >
                                            <Icon
                                              name="WaypointsIcon"
                                              className="h-5 w-5"
                                              aria-hidden="true"
                                            />
                                          </Button>
                                        </Popover>
                                      </Tooltip>
                                    )}
                                  </div>
                                )}
                              {/* ponytail: probe BE canEdit override on inherited child shares.
                                  Select unlocked for non-denied inherited rows; expiry picker stays direct-only (!isInherited below).
                                  Revert: delete this Select block; put Viewer/Editor Select back inside the !mainFileAccess?.isInherited branch with the calendar button. */}
                              {isEditAllowed &&
                                !isFileAccessUserMe(mainFileAccess!) &&
                                !grouped.originalFileAccesses.some(
                                  (fa) => fa.isInherited && fa.fileLimitation.accessDenied,
                                ) && (
                                  <Select
                                    size="small"
                                    className="w-40"
                                    // Mixed may return: coerce away from 'mixed' while option is commented out.
                                    value={grouped.canEdit === 'mixed' ? false : grouped.canEdit}
                                    onChange={(val) => {
                                      grouped.originalFileAccesses.forEach((fa) =>
                                        handleFileLimitationChange(fa, 'canEdit', val),
                                      );
                                    }}
                                    options={canEditOptions}
                                    aria-label={t('common.permissionLevel')}
                                    aria-haspopup="listbox"
                                  />
                                )}
                              {!mainFileAccess?.isInherited &&
                                isEditAllowed &&
                                !isFileAccessUserMe(mainFileAccess!) && (
                                  <Tooltip
                                    placement="top"
                                    title={
                                      grouped.accessExpiration === 'mixed'
                                        ? t('common.editExpirationDate')
                                        : !mainFileAccess?.fileLimitation.accessExpiration
                                          ? t('common.addExpirationDate')
                                          : t('common.editExpirationDate')
                                    }
                                  >
                                    <Button
                                      type="text"
                                      size="small"
                                      disabled={
                                        !isDirectShareExpirationPickerEnabled(
                                          isEditAllowed,
                                          allSelectedFilesWritable,
                                          canElevateToEditor,
                                          grouped.canEdit,
                                        )
                                      }
                                      onClick={() => {
                                        setSelectedFileAccessesForDatePicker(
                                          grouped.originalFileAccesses,
                                        );
                                        const ticksVal =
                                          grouped.accessExpiration !== 'mixed'
                                            ? grouped.accessExpiration
                                            : undefined;
                                        if (ticksVal) {
                                          setFileAccessDatePickerValue(
                                            localDateFromUtcTicks(ticksVal).startOf('day'),
                                          );
                                        } else {
                                          setFileAccessDatePickerValue(null);
                                        }
                                        setIsFileAccessDatePickerOpen(true);
                                      }}
                                      aria-label={
                                        grouped.accessExpiration === 'mixed'
                                          ? t('common.editExpirationDate')
                                          : !mainFileAccess?.fileLimitation.accessExpiration
                                            ? t('common.addExpirationDate')
                                            : t('common.editExpirationDate')
                                      }
                                    >
                                      <Icon
                                        name="CalendarFoldIcon"
                                        className="h-5 w-5"
                                        aria-hidden="true"
                                      />
                                    </Button>
                                  </Tooltip>
                                )}

                              {isEditAllowed &&
                                (grouped.originalFileAccesses.some(
                                  (fa) => fa.isInherited && fa.fileLimitation.accessDenied,
                                ) ? (
                                  isAnyMarkedToRevokeDeny ? (
                                    <Button
                                      color="primary"
                                      size="small"
                                      onClick={() => handleCancelShareAgain(grouped.fileAccessIds)}
                                      aria-label={t('common.cancel')}
                                    >
                                      {t('common.cancel')}
                                    </Button>
                                  ) : (
                                    <Button
                                      aria-label={t('common.shareAgain')}
                                      onClick={() => handleShareAgain(grouped.originalFileAccesses)}
                                      className="text-green-dark"
                                      variant="text"
                                      size="small"
                                    >
                                      {t('common.shareAgain')}
                                    </Button>
                                  )
                                ) : (
                                  <Tooltip placement="top" title={t('common.remove')}>
                                    <Button
                                      onClick={() =>
                                        handleFormFieldChange('fileAccessesToRemove', [
                                          ...form.fileAccessesToRemove,
                                          ...grouped.originalFileAccesses,
                                        ])
                                      }
                                      danger
                                      type="text"
                                      size="small"
                                      aria-label={t('common.remove')}
                                    >
                                      <Icon
                                        name="TrashIcon"
                                        className="h-5 w-5"
                                        aria-hidden="true"
                                      />
                                    </Button>
                                  </Tooltip>
                                ))}
                            </div>
                          )
                        }
                      />
                    );
                  }}
                />
              </div>
              {isLoading && (
                <>
                  <Skeleton avatar paragraph={{ rows: 0 }} active aria-busy="true" />
                  <Skeleton avatar paragraph={{ rows: 0 }} active aria-busy="true" />
                  <Skeleton avatar paragraph={{ rows: 0 }} active aria-busy="true" />
                  <Skeleton avatar paragraph={{ rows: 0 }} active aria-busy="true" />
                  <Skeleton avatar paragraph={{ rows: 0 }} active aria-busy="true" />
                </>
              )}
            </div>
          </OverlayScrollbarsComponent>
        </div>
      )}

      {existingAccessesRequireExpirationError && !areNewUsersOrGroupsSelected && (
        <Alert
          type="warning"
          title={
            <div className="flex w-full flex-row items-center justify-center gap-2">
              <span className="text-yellow-dark text-sm">
                {t('modal.fileShareForm.existingAccessesExpirationRequired')}
              </span>
              {perAccessExpirationPickersAllowBulkMaxExpiration && (
                <Button
                  type="link"
                  disabled={isLoading || !canApplyMaxExpirationToMissingAccesses}
                  size="small"
                  className="text-yellow-dark text-sm! underline!"
                  onClick={handleApplyMaxExpirationToAllMissing}
                  aria-label={t('modal.fileShareForm.applyMaxExpirationForMissingAccesses')}
                  style={{ padding: 0, marginLeft: 8 }}
                >
                  {t('modal.fileShareForm.applyAllExpirationDates')}
                </Button>
              )}
            </div>
          }
          showIcon
          className="my-2"
        />
      )}
      <div
        className="mt-4 flex flex-col gap-2"
        role="group"
        aria-label={t('modal.fileShareForm.actions')}
      >
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-row items-center gap-2">
            {selectedFiles.length === 1 && (
              <Button
                type="text"
                size="small"
                onClick={() => {
                  copyFileLinkToClipboard(selectedFiles?.[0]!);
                  messageApi.success(t('common.linkCopied'));
                }}
                aria-label={t('common.copyLink')}
              >
                <div className="flex items-center gap-2">
                  <Icon name="CopyIcon" className="h-5 w-5" aria-hidden="true" />
                  <span>{t('common.copyLink')}</span>
                </div>
              </Button>
            )}
            {areNewUsersOrGroupsSelected && (
              <Tooltip
                title={
                  form.notificationSettings?.enabled
                    ? t('fileAccessNotification.notificationsEnabled')
                    : t('fileAccessNotification.configureNotifications')
                }
              >
                <Button
                  type="text"
                  size="small"
                  onClick={() => openFileAccessNotificationModal(null)}
                  disabled={isLoading}
                  aria-label={t('fileAccessNotification.configureNotifications')}
                  className="relative inline-flex items-center justify-center"
                >
                  <span className="flex flex-row items-center gap-2">
                    <Icon name="BellIcon" className="h-5 w-5" aria-hidden="true" />
                    <span>{t('common.notifications')}</span>
                    {form.notificationSettings?.enabled && (
                      <span
                        className="bg-azure-light0 absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full"
                        aria-hidden="true"
                      />
                    )}
                  </span>
                </Button>
              </Tooltip>
            )}
          </div>

          <div className="flex flex-row items-center justify-center gap-2">
            <Button size="middle" onClick={handleCancel} aria-label={t('common.cancel')}>
              {t('common.cancel')}
            </Button>
            <Tooltip
              title={
                isSubmitButtonDisabled && submitDisabledReason ? submitDisabledReason : undefined
              }
            >
              <span
                className={
                  isSubmitButtonDisabled && submitDisabledReason ? 'inline-block' : undefined
                }
              >
                <Button
                  size="middle"
                  disabled={isSubmitButtonDisabled}
                  loading={isModalLoading}
                  type="primary"
                  onClick={() => handleSubmit(true)}
                  aria-label={t('common.save')}
                >
                  {areNewUsersOrGroupsSelected ? t('common.share') : t('common.save')}
                </Button>
              </span>
            </Tooltip>
          </div>
        </div>
      </div>
      {/* → Global picker for expiry */}
      <DatePicker
        format="YYYY-MM-DD"
        minDate={dayjs()}
        maxDate={
          newSharingDatePickerMaxDate
            ? dayjs.utc(newSharingDatePickerMaxDate).startOf('day')
            : undefined
        }
        value={limitationsDatePickerValue}
        open={isLimitationsDatePickerOpen}
        onOpenChange={setIsLimitationsDatePickerOpen}
        onChange={(date) => {
          if (!date) return;
          const msEndUtc = dayjs.utc(date.format('YYYY-MM-DD')).endOf('day').valueOf();
          handleFormFieldChange('expiry', msToTicks(msEndUtc));
          setIsLimitationsDatePickerOpen(false);
          setLimitationsDatePickerValue(null);
        }}
        className="pointer-events-none! absolute! opacity-0!"
        aria-label={t('common.expirationDate')}
      />
      {/* → Picker for single fileAccess */}
      <DatePicker
        format="YYYY-MM-DD"
        minDate={dayjs()}
        maxDate={
          (getMaxDateForFileAccessPicker() &&
            dayjs.utc(getMaxDateForFileAccessPicker()).startOf('day')) ||
          (getMaxDateForExistingFileAccess() &&
            dayjs.utc(getMaxDateForExistingFileAccess()).startOf('day')) ||
          (newSharingDatePickerMaxDate && dayjs.utc(newSharingDatePickerMaxDate).startOf('day'))
        }
        value={fileAccessDatePickerValue}
        open={isFileAccessDatePickerOpen}
        onOpenChange={setIsFileAccessDatePickerOpen}
        onChange={(date) => {
          if (!date || !selectedFileAccessesForDatePicker.length) return;
          const msEndUtc = dayjs.utc(date.format('YYYY-MM-DD')).endOf('day').valueOf();
          selectedFileAccessesForDatePicker.forEach((fa) => {
            handleFileLimitationChange(fa, 'accessExpiration', msToTicks(msEndUtc));
          });
          setIsFileAccessDatePickerOpen(false);
          setFileAccessDatePickerValue(null);
          setSelectedFileAccessesForDatePicker([]);
        }}
        className="pointer-events-none! absolute! opacity-0!"
        aria-label={t('common.expirationDate')}
      />
    </Form>
  );
};

export default FileShareForm;
